import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';

import { CustomizeNeeds } from '@/components/OrganiseEvent/CustomizeNeeds';
import { CustomModel } from '@/components/ui/features/hybrid-events/CustomModel/CustomModel';
import { DetailsSection } from '@/components/ui/features/hybrid-events/DetailsSection/DetailsSection';
import { EventExamples } from '@/components/ui/features/hybrid-events/EventExamples/EventExamples';
import { HybridEventsHeading } from '@/components/ui/features/hybrid-events/Heading/Heading';
import { HybridPosters } from '@/components/ui/features/hybrid-events/HybridPosters/HybridPosters';
import { PurpleGradientSection } from '@/components/ui/features/hybrid-events/PurpleGradientSection/PurpleGradientSection';
import { ContactUsSection } from '@/components/ui/features/white-glove-service/ContactUsSection/ContactUsSection';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { JsonLd } from '@/components/ui/shared/seo/JsonLd/JsonLd';
import { AccessFrom } from '@/enums/AccessFrom';
import { queryTestimonials } from '@/queries/testimonals';
import { generateStore } from '@/utils/generateStore';
import { buildBreadcrumbSchema, buildWebPageSchema, getAbsoluteUrl } from '@/utils/structured-data';
import { Hydrate } from '@/utils/swr/Hydrate';

const PAGE_PATH = 'events/hybrid-events';
const PAGE_DESCRIPTION =
  "Unlock the full potential of your scientific conference with Underline's hybrid conferences. Seamlessly blend the immediacy of in-person interactions with the reach of virtual participation for a truly comprehensive event experience.";
const PAGE_TITLE = 'Hybrid Conferences: Where Innovation Meets Inclusion';

const HybridEventsPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ fallback }) => {
  return (
    <Hydrate fallback={fallback}>
      <Layout accessFrom={AccessFrom.Core} navigationVariant="ghost">
        <NextSeo
          title={PAGE_TITLE}
          description={PAGE_DESCRIPTION}
          canonical={getAbsoluteUrl(PAGE_PATH)}
          openGraph={{
            url: getAbsoluteUrl(PAGE_PATH),
            title: PAGE_TITLE,
            description: PAGE_DESCRIPTION,
          }}
        />
        <JsonLd
          id="events-hybrid"
          schema={[
            buildWebPageSchema({
              path: PAGE_PATH,
              name: PAGE_TITLE,
              description: PAGE_DESCRIPTION,
            }),
            buildBreadcrumbSchema([
              { name: 'Home', path: '' },
              { name: 'Events', path: 'events' },
              { name: 'Hybrid Events', path: PAGE_PATH },
            ]),
          ]}
        />
        <HybridEventsHeading />
        <PurpleGradientSection />
        <DetailsSection />
        <CustomModel />
        <EventExamples />
        <CustomizeNeeds />
        <HybridPosters />
        <ContactUsSection />
      </Layout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  await store.fetchResources(queryTestimonials());

  return {
    props: {
      fallback: store.fallback,
    },
  };
};
export default HybridEventsPage;
